/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import cancelTokenFromSignal from '../../helpers/cancelTokenFromSignal';
import getThumbnailUrls from '../../helpers/getThumbnailUrls';
import isAbortedRequest from '../../helpers/isAbortedRequest';
import { setThumbnailUrls, extendAssets } from '../helpers/assets';
import { mergeMasterGalleryAssets } from './helpers/appendMasterGalleryAssets';
import { setMasterAssets } from './helpers/entityAdapter';
import { setMasterAssetsFetching } from './helpers/uiAdapter';

export const fetchAssets = createAsyncThunk(
  'products/fetchAssets',
  async ({ productId }, {
    extra: { Logger, Toast, sdk },
    getState,
    signal,
  }) => {
    try {
      const { team } = getState().user;
      const rootCollectionId = getState().collections?.collections?.my?._id;

      const { data } = await sdk.assets.search({
        tagId: rootCollectionId,
        from: 0,
        size: 250,
        products: productId,
        sort: {
          type: 'custom',
        },
      }, cancelTokenFromSignal(signal));

      if (!data.images?.length) return [];

      const assets = extendAssets(data.images);

      const thumbs = await getThumbnailUrls(assets.map(({ _id }) => _id));

      const assetsWithThumbs = setThumbnailUrls(assets, thumbs, team?.storageType === 's3');

      return assetsWithThumbs.map((asset) => ({
        ...asset,
        permissions: Object
          .keys(asset.permissions || {})
          .reduce((negativePermissions, key) => ({ ...negativePermissions, [key]: false }), {}),
      }));
    } catch (error) {
      if (isAbortedRequest(error, signal)) throw error;
      Logger.log('UI', 'ToastProductsNotLoaded');
      Toast(
        'Products not loaded. Refresh and try again',
        { autoClose: false, type: 'error' },
      );
      Logger.error(new Error('Error products fetching'), { error }, [
        'FetchProductsFailed',
        { errorMessage: 'Products can not be fetched from backend.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    fetchAssets.pending,
    (state, { meta }) => {
      const { productId } = meta.arg;
      state.items.forEach((product) => {
        if (product._id === productId) {
          product.fetchingAssets = true;
        }
      });
      setMasterAssetsFetching(state, productId, true);
    },
  ).addCase(
    fetchAssets.fulfilled,
    (state, { payload: assets, meta }) => {
      const { productId, mergeAssets } = meta.arg;
      const nextAssets = mergeMasterGalleryAssets(assets, mergeAssets, productId);
      state.items.forEach((product) => {
        if (product._id === productId) {
          product.fetchingAssets = false;
          product.assets = nextAssets;
        }
      });
      setMasterAssets(state, productId, nextAssets);
      setMasterAssetsFetching(state, productId, false);
    },
  ).addCase(
    fetchAssets.rejected,
    (state, { error, meta }) => {
      const { productId } = meta.arg;
      state.items.forEach((product) => {
        if (product._id === productId) delete product.fetchingAssets;
      });
      setMasterAssetsFetching(state, productId, false);
      if (meta.aborted) return;
      state.error = error;
    },
  );
};
